import { useState } from 'react'
import { Loader2 } from 'lucide-react'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger' | 'danger-text' | 'outline' | 'link' | 'quiet'
  // Footprint for the solid/bordered variants (primary, secondary, danger,
  // outline). 'md' is the full 50px style-guide button; 'sm' is the compact
  // 36px one. Independent of `variant` so any of those can render compact.
  // Text-style variants (tertiary, danger-text, quiet, link) size to their
  // content and ignore this. Omit to keep each variant's historical default.
  size?: 'sm' | 'md'
  loading?: boolean
  children: React.ReactNode
  className?: string
}

export function Button({
  variant = 'primary',
  size,
  loading = false,
  disabled = false,
  children,
  className = '',
  type = 'button',
  style,
  ...rest
}: ButtonProps) {
  // `loading` and `disabled` both block interaction, but they must NOT look the
  // same: a genuinely disabled button gets the grey treatment, whereas a loading
  // button keeps its active fill (with a spinner) so it reads as "working", not
  // "off" — a grey button under the submit-glow looks broken. `showDisabledStyle`
  // gates only the visual greying; `isDisabled` still governs interactivity.
  const isDisabled = disabled || loading
  const showDisabledStyle = disabled && !loading
  const [hovered, setHovered] = useState(false)

  const baseStyle: React.CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-2)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-body-md)',
    fontWeight: 'var(--weight-semibold)' as unknown as number,
    lineHeight: 'var(--leading-normal)',
    border: 'none',
    cursor: loading ? 'progress' : isDisabled ? 'not-allowed' : 'pointer',
    // Tokens, not literals: CSS's `ease-out` keyword is cubic-bezier(0,0,0.58,1),
    // a different curve from our --ease-out (0,0,0.2,1). Written as 150ms/ease-out
    // this was the one transition in the system easing on a curve the system
    // doesn't define — subtle, but every button hover in all three apps.
    transition:
      'background-color var(--duration-micro) var(--ease-out), color var(--duration-micro) var(--ease-out), opacity var(--duration-micro) var(--ease-out)',
    borderRadius: 'var(--radius-full)',
    height: '50px',
    padding: '0 var(--spacing-8)',
    letterSpacing: '0.01em',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    minWidth: '120px',
  }

  // Footprint for the solid/bordered variants (primary, secondary, danger,
  // outline). Historically size was hard-wired to variant — primary was always
  // 50px, secondary always 36px. It's now a separate `size` axis so any of them
  // can render compact; when `size` is omitted we fall back to each variant's
  // historical default (secondary/outline → sm, everything else → md).
  const solidSize = size ?? (variant === 'secondary' || variant === 'outline' ? 'sm' : 'md')
  const solidSizeStyle: Record<'sm' | 'md', React.CSSProperties> = {
    md: {
      height: '50px',
      padding: '0 var(--spacing-8)',
      fontSize: 'var(--text-body-md)',
      minWidth: '120px',
    },
    sm: {
      height: '36px',
      padding: '0 var(--spacing-4)',
      fontSize: 'var(--text-body-sm)',
      minWidth: 'unset',
    },
  }

  // Size — kept independent of disabled state so a disabled button still has the
  // right dimensions. Solid/bordered variants use the `size` axis above; the
  // text-style variants (tertiary/danger-text/quiet/link) have bespoke layout
  // and ignore `size`.
  const sizeStyle: React.CSSProperties = (() => {
    switch (variant) {
      case 'tertiary':
        return {
          height: 'auto',
          // 12px block padding pulled back by an equal -8px margin. A text
          // button with 4px padding is a 29px-tall tap target — well under the
          // 44px guideline, and "Back" sits at the bottom of a phone screen
          // where thumbs are least accurate. The padding grows the target to
          // 45px; the negative margin returns the layout box to where it was,
          // so the visible label doesn't move (measured: same 730.5px centre
          // line before and after, Continue unmoved). Grow the target, not the
          // ink — don't "tidy" the margin away without replacing the padding.
          padding: 'var(--spacing-3) var(--spacing-2)',
          // Derived, not a literal: the negative margin must always cancel
          // exactly the padding this variant added (spacing-3 instead of the
          // spacing-1 it used to have). Written as the difference of the two
          // tokens, it stays correct if the spacing scale ever moves — a
          // hardcoded -8px would silently stop cancelling and shift the label.
          margin: 'calc(var(--spacing-1) - var(--spacing-3)) 0',
          minWidth: 'unset',
          borderRadius: 0,
          fontSize: 'var(--text-body-sm)',
        }
      case 'danger-text':
        // Destructive text button (e.g. "Remove") — the tertiary footprint in red.
        return {
          height: 'auto',
          padding: 'var(--spacing-1) var(--spacing-2)',
          minWidth: 'unset',
          borderRadius: 0,
          fontSize: 'var(--text-body-sm)',
        }
      // De-emphasized text button for opt-out / dismiss actions that sit
      // alongside a real flow action (e.g. "Don't show again" next to a
      // wizard's step content) — deliberately quieter than tertiary so it
      // doesn't compete with in-flow navigation.
      case 'quiet':
        return {
          height: 'auto',
          padding: '0 var(--spacing-3) var(--spacing-2)',
          minWidth: 'unset',
          borderRadius: 'var(--radius-md)',
        }
      // Link sits flush in running text — render as a real inline element so the
      // underline hugs the text (not the flex line box) and it flows with copy.
      case 'link':
        return {
          display: 'inline',
          height: 'auto',
          padding: 0,
          minWidth: 'unset',
          borderRadius: 0,
          fontSize: 'var(--text-body-sm)',
          lineHeight: 'inherit',
        }
      // primary, secondary, danger, outline — sized by the `size` axis.
      default:
        return solidSizeStyle[solidSize]
    }
  })()

  const colorStyle: React.CSSProperties = (() => {
    if (showDisabledStyle) {
      // Disabled treatment from Figma style guide (node 7995:287):
      // navy 15% fill + navy 40% text (matches --color-text-tertiary).
      return {
        backgroundColor: 'var(--color-outline-variant)',
        color: 'var(--color-text-tertiary)',
      }
    }
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'var(--color-navy-900)',
          color: 'var(--color-white)',
        }
      case 'danger':
        return {
          // The dark stop, not the mid: white on red-500 is 4.18:1 and fails
          // AA. red-700 carries it at 5.79:1. The mid stop is fill-and-border
          // only, never a background for text.
          backgroundColor: 'var(--color-red-700)',
          color: 'var(--color-white)',
        }
      case 'secondary':
        return {
          backgroundColor: 'var(--color-action-primary)',
          color: 'var(--color-text-on-primary)',
        }
      case 'tertiary':
        return {
          backgroundColor: 'transparent',
          color: 'var(--color-action-primary)',
        }
      case 'danger-text':
        return {
          backgroundColor: 'transparent',
          color: 'var(--color-error-text)',
        }
      case 'quiet':
        return {
          backgroundColor: 'transparent',
          color: 'var(--color-text-secondary)',
        }
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: 'var(--color-action-primary)',
          border: '1.5px solid var(--color-action-primary)',
        }
      case 'link':
        return {
          backgroundColor: 'transparent',
          color: 'var(--color-action-primary)',
          textDecoration: 'underline',
        }
    }
  })()

  // Hover fill for the solid / bordered variants (animates via the base
  // background-color transition). Primary and secondary are handled separately
  // by a gradient overlay below — gradients can't be transitioned, so we fade
  // the overlay's opacity instead.
  const hoverStyle: React.CSSProperties = (() => {
    if (isDisabled || !hovered) return {}
    switch (variant) {
      // danger is handled by the gradient-glow overlay below (like primary/secondary).
      case 'outline':
        return { backgroundColor: 'var(--color-action-primary-hover)' }
      // Destructive text button: deepen the red on hover, matching the
      // DocumentCard "Remove" hover (color-only, no background).
      case 'danger-text':
        return { color: 'var(--color-error-text-hover)' }
      // Text buttons deepen from blue to navy on hover (transitions via the
      // base `color` transition).
      case 'tertiary':
      case 'link':
      case 'quiet':
        return { color: 'var(--color-text-primary)' }
      default:
        return {}
    }
  })()

  // Solid variants whose hover is a gradient that fades in over the resting
  // fill (gradients can't be transitioned, so we animate the overlay opacity).
  const hoverGradient: string | null = (() => {
    switch (variant) {
      case 'primary':
        return 'var(--gradient-primary-hover)'
      case 'secondary':
        return 'var(--gradient-action)'
      case 'danger':
        return 'var(--gradient-danger-hover)'
      default:
        return null
    }
  })()

  const combinedStyle: React.CSSProperties = {
    ...baseStyle,
    ...sizeStyle,
    ...colorStyle,
    ...hoverStyle,
    ...style,
  }

  return (
    <button
      type={type}
      disabled={isDisabled}
      className={`group ${className}`}
      style={combinedStyle}
      aria-busy={loading}
      {...rest}
      onMouseEnter={(e) => {
        if (!isDisabled) setHovered(true)
        rest.onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        setHovered(false)
        rest.onMouseLeave?.(e)
      }}
    >
      {/* Primary and secondary get a gradient overlay that fades in on hover (the
          "glow"), with their content layered above it. Every other variant renders
          content directly so inline behaviour (e.g. the Link underline) stays natural. */}
      {hoverGradient ? (
        <>
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: hoverGradient,
              opacity: hovered && !isDisabled ? 1 : 0,
              transition: 'opacity var(--duration-standard) var(--ease-out)',
              pointerEvents: 'none',
            }}
          />
          <span
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {loading && (
              <Loader2 size={16} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} />
            )}
            {children}
          </span>
        </>
      ) : (
        <>
          {loading && (
            <Loader2 size={16} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} />
          )}
          {children}
        </>
      )}
    </button>
  )
}
